import React, { useMemo, useState, useEffect } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTran } from "@/hooks";
import { Checkbox } from "@/components/ui/checkbox";
import { SelectField } from "@/components/common";
import { getRestrictedActions } from "@/helpers";
import { EXCLUDED_PERMISSIONS_IDS } from "@/helpers/permissionConstants";

const ACTIONS = ["view", "create", "update", "delete"];

/** --- UTILS --- */
const normalizeKey = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();
const buildActionKey = (moduleKey, action) =>
  `${normalizeKey(moduleKey)}:${action}`;

const parseActionSet = (values = []) => {
  const set = new Set();
  (Array.isArray(values) ? values : []).forEach((entry) => {
    const normalized = normalizeKey(entry);
    if (!normalized) return;
    const parts = normalized.split(":");
    if (parts.length === 2 && ACTIONS.includes(parts[1])) {
      set.add(buildActionKey(parts[0], parts[1]));
    } else {
      ACTIONS.forEach((action) => set.add(buildActionKey(normalized, action)));
    }
  });
  return set;
};

const normalizeModules = (modules = []) => {
  const mapItem = (item = {}) => {
    const id = normalizeKey(
      item.key || item.id || item._id || item.moduleName?.key,
    );
    const title =
      item?.title || item?.name || item?.value || item?.key || "Module";
    const children = (item.subModule || item.children || []).map(mapItem);
    return { id, title, children };
  };
  return (modules || []).map(mapItem);
};

/** --- SUB-COMPONENT: PERMISSION ROW --- */
const PermissionRow = ({
  item,
  selectedSet,
  onToggle,
  disabled,
  isUserCreatedRole,
  userRole,
}) => {
  const { t } = useTran();

  const canView = selectedSet.has(buildActionKey(item.id, "view"));
  const restrictedActions = getRestrictedActions(
    item.id,
    userRole,
    isUserCreatedRole,
  );

  return (
    <div className="grid grid-cols-2 gap-2 text-[13px] md:grid-cols-4">
      {ACTIONS.map((actionKey) => {
        const actionId = buildActionKey(item.id, actionKey);

        const isView = actionKey === "view";

        const isRestricted = restrictedActions.includes(actionKey);

        const isDisabled = disabled || (!isView && !canView) || isRestricted;

        if (isRestricted) return null;

        return (
          <label
            key={actionId}
            className={cn(
              "flex min-w-0 items-center gap-2 select-none transition-opacity",
              isDisabled
                ? "cursor-not-allowed opacity-30"
                : "cursor-pointer hover:text-blue-600",
            )}
          >
            <Checkbox
              checked={selectedSet.has(actionId)}
              onCheckedChange={(checked) =>
                onToggle?.(item.id, actionKey, Boolean(checked))
              }
              disabled={isDisabled}
              className="h-3.5 w-3.5 shrink-0 rounded-[3px] border-muted-foreground/40"
            />

            <span className="truncate capitalize font-medium leading-none text-muted-foreground">
              {t(actionKey)}
            </span>
          </label>
        );
      })}
    </div>
  );
};

/** --- SUB-COMPONENT: PERMISSION PANEL --- */
const PermissionPanel = ({
  title,
  description,
  modules,
  selectedSet,
  onToggle,
  disabled,
  emptyLabel,
  collapsedMap,
  onToggleCollapse,
  mode,
  onModeChange,
  checkBoxDisable,
  isUserCreatedRole = false,
  userRole = null,
}) => {
  const { t } = useTran();

  return (
    <div className="flex min-h-0 flex-col rounded border border-border overflow-hidden">
      {" "}
      {/* Header with Mode Selector on Right */}
      <div className="flex items-center justify-between border-b border-border/60 p-2.5 gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-foreground truncate">
            {title}
          </p>
          {description && (
            <p className="text-[11px] text-muted-foreground leading-tight truncate">
              {description}
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2 border-l border-border/60 pl-3">
          <label className="text-[11px] font-medium text-muted-foreground whitespace-nowrap">
            {t("Mode")}:
          </label>
          <SelectField
            value={mode}
            onChange={onModeChange}
            options={[
              { key: "inherit", value: t("Inherit") },
              { key: "override", value: t("Override") },
            ]}
            className="h-7 w-[110px] text-[11px] mb-2"
            disabled={disabled}
          />
        </div>
      </div>
      {/* Content Area */}
      <div className="relative h-[200px] overflow-hidden [contain:layout]">
        <div className="absolute inset-0 overflow-x-hidden overflow-y-auto overscroll-contain p-2.5">
          {modules.length === 0 ? (
            <div className="py-4 text-center text-[11px] italic text-muted-foreground">
              {emptyLabel}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {modules.map((item) => {
                const hasChildren = item.children?.length > 0;
                const isCollapsed = Boolean(collapsedMap?.[item.id]);

                return (
                  <div key={item.id} className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => onToggleCollapse?.(item.id)}
                        className="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm text-muted-foreground/60 hover:bg-muted"
                      >
                        {isCollapsed ? (
                          <ChevronRight className="h-3 w-3" />
                        ) : (
                          <ChevronDown className="h-3 w-3" />
                        )}
                      </button>

                      <span className="truncate text-[13px] font-semibold text-foreground/90">
                        {item.title}
                      </span>
                    </div>

                    {!isCollapsed && (
                      <div className="flex flex-col gap-2">
                        {!hasChildren && (
                          <div className="pl-6">
                            <PermissionRow
                              item={item}
                              selectedSet={selectedSet}
                              onToggle={onToggle}
                              disabled={checkBoxDisable || disabled}
                              userRole={userRole}
                              isUserCreatedRole={isUserCreatedRole}
                            />
                          </div>
                        )}

                        {hasChildren && (
                          <div className="ml-2 flex flex-col gap-3 border-l border-dashed border-border/50 pl-4 py-1">
                            {item.children.map((child) => (
                              <div
                                key={child.id}
                                className="flex flex-col gap-1.5"
                              >
                                <div className="flex min-w-0 items-center gap-2 text-[12px] font-medium text-muted-foreground">
                                  <span className="select-none opacity-30">
                                    └
                                  </span>

                                  <span className="truncate">
                                    {child.title}
                                  </span>
                                </div>

                                <div className="pl-4">
                                  <PermissionRow
                                    item={child}
                                    selectedSet={selectedSet}
                                    onToggle={onToggle}
                                    disabled={checkBoxDisable || disabled}
                                    userRole={userRole}
                                    isUserCreatedRole={isUserCreatedRole}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/** --- MAIN COMPONENT --- */
const PermissionOverridesSection = ({
  isUserCreatedRole = false,
  userRole = null,
  value,
  modules = [],
  onChange,
  disabled = false,
  checkBoxDisable = false,
}) => {
  const { t } = useTran();
  const [collapsedMap, setCollapsedMap] = useState({});

  const mode = value?.mode === "override" ? "override" : "inherit";
  const allowSet = useMemo(() => parseActionSet(value?.allow), [value?.allow]);
  const denySet = useMemo(() => parseActionSet(value?.deny), [value?.deny]);
  const normalizedModules = useMemo(() => normalizeModules(modules), [modules]);

  const filteredModules = useMemo(() => {
    return normalizedModules.filter(
      (mod) => !EXCLUDED_PERMISSIONS_IDS.includes(mod.id),
    );
  }, [normalizedModules]);

  useEffect(() => {
    if (normalizedModules.length === 0) return;
    setCollapsedMap((prev) => {
      const next = { ...prev };
      normalizedModules.forEach((m) => {
        if (!(m.id in next)) next[m.id] = true;
      });
      return next;
    });
  }, [normalizedModules]);

  const emitChange = (nextAllow, nextDeny, nextMode = mode) => {
    onChange?.({
      mode: nextMode,
      allow: Array.from(nextAllow),
      deny: Array.from(nextDeny),
    });
  };

  /** Your Original handleModeChange */
  const handleModeChange = (input) => {
    const nextMode = input?.target ? input.target.value : input;
    emitChange(allowSet, denySet, nextMode);
  };

  const handleAllowToggle = (moduleKey, action, checked) => {
    const actionKey = buildActionKey(moduleKey, action);
    const nextAllow = new Set(allowSet);
    const nextDeny = new Set(denySet);
    if (checked) {
      nextAllow.add(actionKey);
      nextDeny.delete(actionKey);
    } else {
      nextAllow.delete(actionKey);
    }
    emitChange(nextAllow, nextDeny);
  };

  const handleToggleCollapse = (id) =>
    setCollapsedMap((p) => ({ ...p, [id]: !p[id] }));

  return (
    <div className="w-full">
      <PermissionPanel
        title={t("Allow actions")}
        modules={filteredModules}
        selectedSet={allowSet}
        onToggle={handleAllowToggle}
        disabled={disabled}
        emptyLabel={t("No modules available.")}
        collapsedMap={collapsedMap}
        onToggleCollapse={handleToggleCollapse}
        mode={mode}
        onModeChange={handleModeChange}
        checkBoxDisable={checkBoxDisable}
        userRole={userRole}
        isUserCreatedRole={isUserCreatedRole}
      />
    </div>
  );
};

export default PermissionOverridesSection;
